import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  Page,
  PageHeader,
  StatCard,
  StatCardList,
} from '@evogard/ui';
import {
  count,
  format as formatMoney,
  loadFailureMessage,
  sum,
  type Money,
} from '@evogard/domain';
import type { ClaimsTeamMapNodeWire } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';
import { useFullscreen } from '../../../lib/fullscreen.js';
import { useStoredFlag } from '../../../lib/stored-flag.js';
import {
  TeamMap,
  TeamMapLegendDot,
  TeamMapViewToggle,
  findNode,
  leavesOf,
  type TeamMapColumn,
  type TeamMapDot,
} from '../../../components/team-map/TeamMap.js';
import {
  PeriodFields,
  initialPeriod,
  periodRange,
} from '../../../components/team-map/period.js';
import { compact, money } from '../../financeiro/dashboard/money.js';

type Node = ClaimsTeamMapNodeWire;
type Measure = 'quantidade' | 'custo';
type Kind = 'assistencia' | 'periferico' | 'sinistro';

const KINDS: { key: Kind; label: string; dot: TeamMapDot }[] = [
  { key: 'assistencia', label: 'Assistência 24h', dot: 'fase-azul' },
  { key: 'periferico', label: 'Periférico', dot: 'fase-turquesa' },
  { key: 'sinistro', label: 'Sinistro', dot: 'fase-vermelho' },
];

const fmtNumber = (v: number) => v.toLocaleString('pt-BR');
/** A frequência é pequena — 0,4 por 100 carros num mês —, e o arredondamento a uma casa a apagaria. */
const fmtRate = (v: number) =>
  v > 0 && v < 0.01
    ? '< 0,01'
    : v.toLocaleString('pt-BR', { maximumFractionDigits: v < 1 ? 2 : 1 });

/**
 * O mapa do time no Sinistro (29/10/2026): as ocorrências do período — 24h,
 * periférico e sinistro —, o custo delas e a sinistralidade, pela montagem de
 * hoje do comercial, em organograma ou em tabela.
 *
 * **A sinistralidade é a FREQUÊNCIA**: ocorrências por 100 veículos da
 * carteira. A de dinheiro (custo sobre a receita) espera a mensalidade entrar
 * no sistema — decisão do dono em 29/10/2026. E a pílula não pinta faixa de
 * bom e ruim: ninguém decidiu onde ela fica (regra 14).
 *
 * As regras do que é ocorrência e de quando estão em
 * `apps/api/src/modules/claims/claims.team-map.service.ts`.
 */
export function ClaimsTeamMapScreen() {
  const [period, setPeriod] = useState(initialPeriod);
  const [view, setView] = useState<'organograma' | 'tabela'>('organograma');
  const [measure, setMeasure] = useState<Measure>('quantidade');
  const [kinds, setKinds] = useState<Record<Kind, boolean>>({
    assistencia: true,
    periferico: true,
    sinistro: true,
  });
  const [thirdParty, setThirdParty] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  /* A largura é preferência de quem olha: fica no navegador. A tela cheia não. */
  const [wide, setWide] = useStoredFlag('sinistro.mapa-do-time.largura-total', false);
  const fullscreen = useFullscreen<HTMLDivElement>();

  const range = periodRange(period);
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const query = { from: range.from, to: range.to };
  const map = useQuery({
    queryKey: ['claims', 'team-map', tenantId, query],
    queryFn: async () =>
      ok(
        await api.claimsTeamMap.teamMap({ query }),
        200,
        loadFailureMessage(undefined, 'o mapa do time'),
      ),
    enabled: tenantId !== undefined && !range.incomplete && !range.inverted,
    placeholderData: (previous) => previous,
  });
  const nodes = useMemo(() => map.data?.nodes ?? [], [map.data]);
  const root = nodes.find((n) => n.parentKey === null);

  /* O sinistro de terceiro soma no sinistro só quando a chave está ligada. */
  const tally = useCallback(
    (n: Node, k: Kind) => {
      const v = n.values[k];
      if (k !== 'sinistro' || !thirdParty)
        return { count: v.count, amount: money(v.amount) };
      return {
        count: v.count + n.values.terceiro.count,
        amount: sum(money(v.amount), money(n.values.terceiro.amount)),
      };
    },
    [thirdParty],
  );
  const active = KINDS.filter((k) => kinds[k.key]);
  const occurrences = useCallback(
    (n: Node) => active.reduce((acc, k) => acc + tally(n, k.key).count, 0),
    [active, tally],
  );
  const cost = useCallback(
    (n: Node) => sum(...active.map((k) => tally(n, k.key).amount)),
    [active, tally],
  );
  const rate = useCallback(
    (n: Node) => {
      const fleet = n.values.veiculos.count;
      return fleet > 0 ? (occurrences(n) / fleet) * 100 : null;
    },
    [occurrences],
  );

  const columns = useMemo<TeamMapColumn<Node>[]>(
    () => [
      {
        key: 'veiculos',
        label: 'Veículos',
        value: (n) => n.values.veiculos.count,
        format: fmtNumber,
      },
      ...active.map((k): TeamMapColumn<Node> => ({
        key: k.key,
        label:
          k.key === 'sinistro' && thirdParty
            ? 'Sinistro + terc.'
            : k.label.replace('Assistência ', ''),
        dot: k.dot,
        value: (n) =>
          measure === 'quantidade' ? tally(n, k.key).count : tally(n, k.key).amount,
        format: measure === 'quantidade' ? fmtNumber : (v) => compact(v as Money),
        bar: true,
        inCard: true,
      })),
      measure === 'quantidade'
        ? { key: 'total', label: 'Ocorrências', value: occurrences, format: fmtNumber }
        : {
            key: 'total',
            label: 'Custo',
            value: cost,
            format: (v) => compact(v as Money),
          },
    ],
    [active, measure, thirdParty, tally, occurrences, cost],
  );
  const badge = useCallback(
    (n: Node) => {
      const r = rate(n);
      return r === null ? null : { tone: 'neutro' as const, text: `${fmtRate(r)} / 100` };
    },
    [rate],
  );
  const rank = useCallback((n: Node) => occurrences(n), [occurrences]);

  const toggleKind = (k: Kind) =>
    setKinds((prev) => {
      const next = { ...prev, [k]: !prev[k] };
      return Object.values(next).some(Boolean) ? next : prev;
    });

  const rootRate = root ? rate(root) : undefined;

  return (
    <Page measure="cheia">
      <PageHeader title="Mapa do time" />

      <div className="flex flex-wrap items-end gap-3">
        <PeriodFields value={period} onChange={setPeriod} />
        <div className="flex items-center gap-1" role="group" aria-label="Medida">
          {(['quantidade', 'custo'] as const).map((m) => (
            <Button
              key={m}
              variant={measure === m ? 'secundario' : 'discreto'}
              size="sm"
              aria-pressed={measure === m}
              onClick={() => setMeasure(m)}
            >
              {m === 'quantidade' ? 'Quantidade' : 'Custo (R$)'}
            </Button>
          ))}
        </div>
        <div
          className="flex flex-wrap items-center gap-1"
          role="group"
          aria-label="Ocorrências"
        >
          {KINDS.map((k) => (
            <Button
              key={k.key}
              variant={kinds[k.key] ? 'secundario' : 'discreto'}
              size="sm"
              aria-pressed={kinds[k.key]}
              onClick={() => toggleKind(k.key)}
            >
              <TeamMapLegendDot dot={k.dot} />
              {k.label}
            </Button>
          ))}
        </div>
        <Button
          variant={thirdParty ? 'secundario' : 'discreto'}
          size="sm"
          aria-pressed={thirdParty}
          onClick={() => setThirdParty((v) => !v)}
        >
          Somar sinistro de terceiro
        </Button>
      </div>

      {range.inverted && (
        <Alert tone="erro">O início do período vem depois do fim.</Alert>
      )}
      {map.error && <Alert tone="erro">{map.error.message}</Alert>}

      <StatCardList
        stretch
        className="xl:[grid-template-columns:repeat(5,minmax(0,1fr))]"
      >
        <StatCard
          icon="veiculo"
          label="Veículos"
          value={root ? fmtNumber(root.values.veiculos.count) : undefined}
          support="a carteira de hoje"
        />
        {active.map((k) => (
          <StatCard
            key={k.key}
            icon="atencao"
            label={k.key === 'sinistro' && thirdParty ? 'Sinistro + terceiro' : k.label}
            legend={k.dot}
            value={root ? fmtNumber(tally(root, k.key).count) : undefined}
            support={
              root ? `${formatMoney(tally(root, k.key).amount)} de custo` : undefined
            }
          />
        ))}
        <StatCard
          icon="grafico"
          label="Sinistralidade"
          value={
            rootRate === undefined
              ? undefined
              : rootRate === null
                ? '—'
                : `${fmtRate(rootRate)} / 100`
          }
          support="ocorrências por 100 veículos"
        />
      </StatCardList>

      {/*
       * A área que cresce. "Largura total" tira o detalhe do lado e o põe
       * embaixo, e o organograma fica mais alto; "Tela cheia" leva o cartão e o
       * detalhe para a tela inteira, com o detalhe de volta ao lado.
       */}
      <div
        ref={fullscreen.ref}
        className={cn(
          fullscreen.active && 'overflow-auto bg-fundo p-4',
          fullscreen.pseudo && 'fixed inset-0 z-50',
        )}
      >
        <div
          className={cn(
            'grid items-start gap-4',
            (fullscreen.active || !wide) && 'lg:grid-cols-[minmax(0,1fr)_20rem]',
          )}
        >
          <Card
            bleed
            title={view === 'organograma' ? 'Organograma do time' : 'Tabela do time'}
            descricao="A montagem de hoje. Clique num cartão para ver o detalhe."
            actions={
              <div className="flex flex-wrap items-center justify-end gap-1">
                {!fullscreen.active && (
                  <Button
                    variant="discreto"
                    size="sm"
                    icon="mover"
                    className="max-lg:hidden"
                    aria-pressed={wide}
                    onClick={() => setWide(!wide)}
                  >
                    {wide ? 'Detalhe ao lado' : 'Largura total'}
                  </Button>
                )}
                <Button
                  variant={fullscreen.active ? 'secundario' : 'discreto'}
                  size="sm"
                  icon={fullscreen.active ? 'fechar' : 'expandir'}
                  aria-pressed={fullscreen.active}
                  title={fullscreen.active ? 'Sair da tela cheia (Esc)' : 'Tela cheia'}
                  onClick={fullscreen.toggle}
                >
                  {fullscreen.active ? 'Sair da tela cheia' : 'Tela cheia'}
                </Button>
                <TeamMapViewToggle value={view} onChange={setView} />
              </div>
            }
          >
            {map.isLoading ? (
              <p className="p-4 text-sm text-texto-suave">Carregando o mapa…</p>
            ) : nodes.length > 0 ? (
              <TeamMap
                view={view}
                nodes={nodes}
                columns={columns}
                badge={badge}
                rank={rank}
                footer={(n) => count(n.values.veiculos.count, 'veículo', 'veículos')}
                selected={selected}
                onSelect={setSelected}
                height={
                  fullscreen.active ? 'calc(100dvh - 7rem)' : wide ? '46rem' : undefined
                }
              />
            ) : null}
          </Card>
          <Detail
            nodes={nodes}
            selected={selected}
            onSelect={setSelected}
            kinds={active}
            tally={tally}
            rate={rate}
          />
        </div>
      </div>

      <p className="text-xs text-texto-suave">
        O número vai para o consultor que está no veículo hoje, e a árvore é a de hoje.
        Conta a assistência acionada, o periférico e o sinistro abertos no período; o
        custo é o das despesas lançadas no Financeiro para cada um, e o da assistência é o
        valor do acionamento. A sinistralidade é a de frequência — por 100 veículos da
        carteira — até a mensalidade entrar no sistema.
      </p>
    </Page>
  );
}

function Detail({
  nodes,
  selected,
  onSelect,
  kinds,
  tally,
  rate,
}: {
  nodes: readonly Node[];
  selected: string | null;
  onSelect: (key: string) => void;
  kinds: readonly { key: Kind; label: string; dot: TeamMapDot }[];
  tally: (n: Node, k: Kind) => { count: number; amount: Money };
  rate: (n: Node) => number | null;
}) {
  const { node, parent } = findNode(nodes, selected);
  if (!node) return null;
  const r = rate(node);
  const leaves = leavesOf(nodes, node.key)
    .filter((n) => n.kind === 'consultor' && rate(n) !== null)
    .sort((a, b) => (rate(b) ?? 0) - (rate(a) ?? 0))
    .slice(0, 5);
  return (
    /* Trocar de cartão troca a gaveta num esmaecer curto, e não num corte seco. */
    <Card
      key={node.key}
      className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
      title={node.label}
      descricao={
        parent && parent.kind === 'time' ? `${node.sub} · ${parent.label}` : node.sub
      }
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutro">
            {r === null ? 'Sem carteira' : `${fmtRate(r)} / 100 veículos`}
          </Badge>
          <span className="text-xs text-texto-suave">
            {count(node.values.veiculos.count, 'veículo', 'veículos')}
          </span>
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 text-sm">
          {kinds.map((k) => {
            const t = tally(node, k.key);
            return (
              <div key={k.key} className="contents">
                <dt className="flex items-center gap-2 text-texto-suave">
                  <TeamMapLegendDot dot={k.dot} />
                  {k.label}
                </dt>
                <dd className="text-right font-semibold tabular-nums">
                  {fmtNumber(t.count)}
                  <span className="block text-xs font-normal text-texto-tenue">
                    {formatMoney(t.amount)}
                  </span>
                </dd>
              </div>
            );
          })}
        </dl>
        {leaves.length > 0 && (
          <div className="grid gap-2">
            <p className="rotulo text-texto-suave">Maior frequência aqui dentro</p>
            <ul className="grid divide-y divide-borda rounded-borda border border-borda">
              {leaves.map((l) => (
                <li key={l.key}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-superficie-suave"
                    onClick={() => onSelect(l.key)}
                  >
                    <span className="min-w-0 truncate">{l.label}</span>
                    <Badge tone="neutro">{fmtRate(rate(l) ?? 0)} / 100</Badge>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
